import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Table,
  message,
  Divider,
  Row,
  Col,
  Card,
  Typography
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import apiService from '@/services/apiService';
import {
  recurringVendorBillService,
  type RecurringVendorBill,
  type CreateRecurringVendorBillInput,
  type RecurringVendorBillItem,
  type RecurringFrequency
} from '@/services/vendorBillService';

const { Text } = Typography;

interface FormItem {
  key: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  accountId: string;
  subtotal: number;
}

interface Vendor {
  id: string;
  name: string;
  code: string;
}

interface Account {
  id: string;
  name: string;
  code: string;
}

interface Props {
  initialValues?: RecurringVendorBill | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const frequencyOptions = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'YEARLY', label: 'Yearly' }
];

const dayOfWeekOptions = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const RecurringVendorBillForm: React.FC<Props> = ({ initialValues, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [items, setItems] = useState<FormItem[]>([]);
  const [frequency, setFrequency] = useState<RecurringFrequency>('MONTHLY');

  useEffect(() => {
    fetchVendors();
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        name: initialValues.name,
        vendorId: initialValues.vendorId,
        frequency: initialValues.frequency,
        startDate: dayjs(initialValues.startDate),
        endDate: initialValues.endDate ? dayjs(initialValues.endDate) : null,
        dayOfMonth: initialValues.dayOfMonth,
        dayOfWeek: initialValues.dayOfWeek
      });
      setFrequency(initialValues.frequency);

      // Load items
      if (initialValues.items && initialValues.items.length > 0) {
        setItems(initialValues.items.map((item, index) => ({
          key: `item-${index}`,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount || 0,
          accountId: item.accountId,
          subtotal: item.subtotal
        })));
      } else {
        addNewItem();
      }
    } else {
      addNewItem();
    }
  }, [initialValues, form]);

  const fetchVendors = async () => {
    try {
      const response: any = await apiService.get('/accounting/vendors');
      setVendors(response.vendors || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response: any = await apiService.get('/accounting/accounts');
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const addNewItem = () => {
    const newItem: FormItem = {
      key: `item-${Date.now()}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      accountId: '',
      subtotal: 0
    };
    setItems([...items, newItem]);
  };

  const removeItem = (key: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.key !== key));
    }
  };

  const updateItem = (key: string, field: keyof FormItem, value: any) => {
    setItems(items.map(item => {
      if (item.key === key) {
        const updated = { ...item, [field]: value };
        // Recalculate subtotal
        updated.subtotal = (updated.quantity * updated.unitPrice) - updated.discount;
        return updated;
      }
      return item;
    }));
  };

  const calculateTotals = () => {
    const amountUntaxed = items.reduce((sum, item) => sum + item.subtotal, 0);
    return {
      amountUntaxed,
      amountTax: 0, // No tax calculation for now
      amountTotal: amountUntaxed
    };
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      // Validate items
      const validItems = items.filter(item => item.description && item.accountId);
      if (validItems.length === 0) {
        message.error('Please add at least one line item');
        return;
      }

      setLoading(true);
      const values = form.getFieldsValue();
      const totals = calculateTotals();

      const data: CreateRecurringVendorBillInput = {
        name: values.name,
        vendorId: values.vendorId,
        frequency: values.frequency,
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
        dayOfMonth: values.frequency === 'MONTHLY' || values.frequency === 'QUARTERLY' || values.frequency === 'YEARLY'
          ? values.dayOfMonth
          : undefined,
        dayOfWeek: values.frequency === 'WEEKLY' ? values.dayOfWeek : undefined,
        amountUntaxed: totals.amountUntaxed,
        amountTax: totals.amountTax,
        amountTotal: totals.amountTotal,
        items: validItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount,
          accountId: item.accountId,
          subtotal: item.subtotal
        }))
      };

      if (initialValues) {
        await recurringVendorBillService.update(initialValues.id, data);
        message.success('Recurring bill updated successfully');
      } else {
        await recurringVendorBillService.create(data);
        message.success('Recurring bill created successfully');
      }

      onSuccess();
    } catch (error: any) {
      message.error(error.message || 'Failed to save recurring bill');
    } finally {
      setLoading(false);
    }
  };

  const itemColumns: ColumnsType<FormItem> = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (_, record) => (
        <Input
          value={record.description}
          placeholder="Enter description"
          onChange={(e) => updateItem(record.key, 'description', e.target.value)}
        />
      )
    },
    {
      title: 'Account',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 180,
      render: (_, record) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Select account"
          value={record.accountId || undefined}
          onChange={(value) => updateItem(record.key, 'accountId', value)}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
          options={accounts.map(a => ({
            value: a.id,
            label: `${a.code} - ${a.name}`
          }))}
        />
      )
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      render: (_, record) => (
        <InputNumber
          min={1}
          value={record.quantity}
          onChange={(value) => updateItem(record.key, 'quantity', value || 1)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 100,
      render: (_, record) => (
        <InputNumber
          min={0}
          precision={2}
          value={record.unitPrice}
          onChange={(value) => updateItem(record.key, 'unitPrice', value || 0)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      width: 90,
      render: (_, record) => (
        <InputNumber
          min={0}
          precision={2}
          value={record.discount}
          onChange={(value) => updateItem(record.key, 'discount', value || 0)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 100,
      align: 'right',
      render: (value) => <Text strong>${value.toFixed(2)}</Text>
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.key)}
          disabled={items.length <= 1}
        />
      )
    }
  ];

  const totals = calculateTotals();

  return (
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="name"
            label="Template Name"
            rules={[{ required: true, message: 'Please enter a template name' }]}
          >
            <Input placeholder="e.g., Monthly Office Rent" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="vendorId"
            label="Vendor"
            rules={[{ required: true, message: 'Please select a vendor' }]}
          >
            <Select
              placeholder="Select vendor"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={vendors.map(v => ({
                value: v.id,
                label: `${v.code} - ${v.name}`
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="frequency"
            label="Frequency"
            rules={[{ required: true }]}
            initialValue="MONTHLY"
          >
            <Select
              options={frequencyOptions}
              onChange={(value) => setFrequency(value)}
            />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="startDate"
            label="Start Date"
            rules={[{ required: true, message: 'Please select start date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="endDate"
            label="End Date (Optional)"
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      {/* Conditional fields based on frequency */}
      {(frequency === 'MONTHLY' || frequency === 'QUARTERLY' || frequency === 'YEARLY') && (
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="dayOfMonth"
              label="Day of Month"
              rules={[{ required: true, message: 'Please select day of month' }]}
            >
              <Select
                placeholder="Select day"
                options={Array.from({ length: 28 }, (_, i) => ({
                  value: i + 1,
                  label: `${i + 1}${['st', 'nd', 'rd'][i] || 'th'}`
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {frequency === 'WEEKLY' && (
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="dayOfWeek"
              label="Day of Week"
              rules={[{ required: true, message: 'Please select day of week' }]}
            >
              <Select
                placeholder="Select day"
                options={dayOfWeekOptions}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Divider>Line Items</Divider>

      <Table
        dataSource={items}
        columns={itemColumns}
        rowKey="key"
        pagination={false}
        size="small"
        footer={() => (
          <Button
            type="dashed"
            onClick={addNewItem}
            icon={<PlusOutlined />}
            style={{ width: '100%' }}
          >
            Add Line Item
          </Button>
        )}
      />

      {/* Totals */}
      <Card size="small" style={{ marginTop: 16 }}>
        <Row justify="end">
          <Col span={8}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text>Subtotal:</Text>
              <Text strong>${totals.amountUntaxed.toFixed(2)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text>Tax:</Text>
              <Text>${totals.amountTax.toFixed(2)}</Text>
            </div>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 16 }}>Total:</Text>
              <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                ${totals.amountTotal.toFixed(2)}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Actions */}
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {initialValues ? 'Update' : 'Create'} Recurring Bill
          </Button>
        </Space>
      </div>
    </Form>
  );
};

export default RecurringVendorBillForm;